import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import type { DeviceInfo, MeasurementPoint } from "@energyplatform/client";
import { api } from "../services.ts";
import { t, tOr } from "../i18n.ts";
import { depthFirst, reloadTree, scope, siteTree } from "../context.ts";
import { ErrorBox } from "./ErrorBox.tsx";
import { hasValue } from "../model.ts";
import { wholeDevicePoints, type Member } from "../placing.ts";
import { Dialog, useAction } from "./ui.tsx";

/** Points that currently measure the device, with the place each one is in. */
export function placesOf(
  deviceId: string,
): { point: MeasurementPoint; nodeName: string }[] {
  const tree = siteTree();
  return (tree?.points ?? [])
    .filter((p) => p.binding?.deviceId === deviceId)
    .map((point) => ({
      point,
      nodeName: tree!.nodes.find((n) => n.id === point.nodeId)?.name ?? "",
    }));
}

/**
 * Puts a device in a place (doc 12): a new measurement point in the chosen node measured by the device, or an
 * existing point without a device. Opened from a device card (device given) or from a place (node given).
 */
export function PlaceDeviceDialog(props: {
  device?: Pick<DeviceInfo, "id" | "name" | "category">;
  nodeId?: string;
  onClose: () => void;
  onSaved?: (point: MeasurementPoint) => void;
}) {
  const [devices] = createResource<
    Pick<DeviceInfo, "id" | "name" | "category">[]
  >(() =>
    props.device ? Promise.resolve([props.device]) : api.devices.list(),
  );
  const [deviceId, setDeviceId] = createSignal(props.device?.id ?? "");
  const device = () => devices()?.find((d) => d.id === deviceId());
  const [detail] = createResource(deviceId, (id) =>
    id ? api.devices.get(id) : Promise.resolve(undefined),
  );
  const channels = () => detail()?.template.channels ?? [];

  const nodes = () => siteTree()?.nodes ?? [];
  const [nodeId, setNodeId] = createSignal(
    props.nodeId ?? scope()?.id ?? nodes()[0]?.id ?? "",
  );
  const pointsHere = () =>
    (siteTree()?.points ?? []).filter((p) => p.nodeId === nodeId());
  const free = () => pointsHere().filter((p) => p.binding === null);

  const [mode, setMode] = createSignal<"new" | "existing">("new");
  const [pointId, setPointId] = createSignal("");
  const [name, setName] = createSignal<string>();
  const [channel, setChannel] = createSignal("");
  const [medium, setMedium] = createSignal<string>();
  const [role, setRole] = createSignal<string>();
  const [useLocation, setUseLocation] = createSignal(true);
  const [whole, setWhole] = createSignal(true);
  const [run, error, busy] = useAction();

  // What the integration says about the installation (e.g. a Lavva meter marked as PV, with coordinates).
  const hint = () =>
    detail()
      ?.sources.map((s) => s.placement)
      .find((h) => h !== null) ?? null;
  const node = () => nodes().find((n) => n.id === nodeId());
  const located = () => {
    const h = hint();
    return h?.latitude != null &&
      h.longitude != null &&
      node() &&
      node()!.latitude === null
      ? { latitude: h.latitude, longitude: h.longitude }
      : null;
  };

  const chosenChannel = () =>
    channel() ||
    (channels().find((c) => c.key === "main") ?? channels()[0])?.key ||
    "";
  // What the chosen channel measures decides what can be chosen: a sensor has its quantity and no role.
  const kind = () =>
    detail()?.points.find((p) => p.channel === chosenChannel());
  const condition = () => kind()?.kind === "condition";
  const roles = () =>
    kind()?.roles.length
      ? kind()!.roles
      : ["main", "source", "storage", "sub", "load", "environment"];
  const mediaChoice = () =>
    kind()?.media.length
      ? kind()!.media
      : ["el", "gas", "water", "temp", "pv", "bess", "air"];
  const pick = (value: string | undefined, allowed: string[]) =>
    value && allowed.includes(value) ? value : undefined;
  const defaultRole = () =>
    pick(hint()?.role ?? undefined, roles()) ??
    (condition()
      ? "environment"
      : roles().includes("main") && !pointsHere().some((p) => p.role === "main")
        ? "main"
        : roles().includes("load")
          ? "load"
          : roles()[0]!);
  const defaultMedium = () =>
    condition()
      ? kind()!.medium
      : (pick(hint()?.medium ?? undefined, mediaChoice()) ??
        kind()?.medium ??
        "el");
  const chosenRole = () => pick(role(), roles()) ?? defaultRole();
  const chosenMedium = () => pick(medium(), mediaChoice()) ?? defaultMedium();
  const chosenPoint = () => pointId() || free()[0]?.id || "";

  // The whole vendor device: this device's measured channels, then the other channels of the same physical device
  // (bound and not placed yet), e.g. every meter of a Lavva device or a SUPLA meter's total and phases.
  const [members] = createResource(detail, async (d): Promise<Member[]> => {
    const measured = async (id: string) => [
      ...new Set(
        (await api.devices.latest(id)).filter(hasValue).map((v) => v.channel),
      ),
    ];
    const self: Member = {
      deviceId: d.id,
      name: d.name,
      channels: await measured(d.id),
      kinds: d.points,
    };
    const all = await api.devices.list();
    const siblings: Member[] = [];
    for (const source of d.sources) {
      const seen = await api.connections.discovered(source.connectionId);
      const parent = seen.find(
        (c) => c.sourceDeviceId === source.sourceDeviceId,
      )?.parent?.id;
      for (const c of seen) {
        const id = c.boundDeviceId;
        if (
          parent &&
          c.parent?.id === parent &&
          id &&
          id !== d.id &&
          placesOf(id).length === 0 &&
          !siblings.some((s) => s.deviceId === id)
        ) {
          siblings.push({
            deviceId: id,
            name: all.find((x) => x.id === id)?.name ?? c.name,
            channels: await measured(id),
            kinds: (await api.devices.get(id)).points,
          });
        }
      }
    }
    return [self, ...siblings.sort((a, b) => a.name.localeCompare(b.name))];
  });
  const plan = createMemo(() =>
    wholeDevicePoints(
      members() ?? [],
      {
        name: (name() ?? device()?.name ?? "").trim(),
        role: chosenRole(),
        medium: chosenMedium(),
      },
      (key) => (key === "main" ? t("place.total") : tOr(`channel.${key}`, key)),
    ),
  );
  const placingWhole = () => mode() === "new" && whole() && plan().length > 1;
  // Only meters measure parts of each other; a multisensor's channels are separate quantities.
  const planHasMeters = () =>
    plan().filter((p) => p.kind !== "condition").length > 1;

  const save = (e: SubmitEvent) => {
    e.preventDefault();
    void run(async () => {
      const binding = {
        deviceId: deviceId(),
        channel: chosenChannel(),
        invert: false,
      };
      const point = placingWhole()
        ? (
            await api.sites.createPoints(
              nodeId(),
              plan().map(({ name: n, medium: m, role: r, device: d }) => ({
                name: n,
                medium: m,
                role: r,
                device: d,
              })),
            )
          )[0]!
        : mode() === "existing"
          ? await api.sites
              .bindPoint(
                chosenPoint(),
                binding.deviceId,
                binding.channel,
                false,
                null,
              )
              .then(() => free().find((p) => p.id === chosenPoint())!)
          : await api.sites.createPoint(
              nodeId(),
              (name() ?? device()?.name ?? "").trim(),
              chosenMedium(),
              chosenRole(),
              binding,
            );
      const position = located();
      if (position && useLocation()) {
        const n = node()!;
        await api.sites.updateNode(n.id, {
          parentId: n.parentId,
          name: n.name,
          kind: n.kind,
          timeZone: n.timeZone,
          ...position,
        });
      }
      await reloadTree();
      props.onSaved?.(point);
      props.onClose();
    });
  };

  return (
    <Dialog
      title={
        props.device
          ? t("place.title", { name: props.device.name })
          : t("place.titleNode")
      }
      onClose={props.onClose}
    >
      <Show
        when={nodes().length > 0}
        fallback={<p class="muted">{t("place.noNodes")}</p>}
      >
        <form class="stack" onSubmit={save}>
          <Show when={!props.device}>
            <div class="field">
              <label for="placeDevice">{t("assets.device")}</label>
              <select
                id="placeDevice"
                required
                value={deviceId()}
                onChange={(e) => {
                  setDeviceId(e.currentTarget.value);
                  setChannel("");
                  setName(undefined);
                }}
              >
                <option value="" disabled>
                  {t("assets.chooseDevice")}
                </option>
                <For
                  each={[...(devices() ?? [])].sort(
                    (a, b) =>
                      Number(placesOf(a.id).length > 0) -
                        Number(placesOf(b.id).length > 0) ||
                      a.name.localeCompare(b.name),
                  )}
                >
                  {(d) => (
                    <option value={d.id}>
                      {d.name} · {t(`category.${d.category}`)}
                      {placesOf(d.id).length > 0
                        ? ` · ${t("place.alreadyPlaced")}`
                        : ""}
                    </option>
                  )}
                </For>
              </select>
            </div>
          </Show>
          <Show when={!props.nodeId}>
            <div class="field">
              <label for="placeNode">{t("assets.node")}</label>
              <select
                id="placeNode"
                value={nodeId()}
                onChange={(e) => setNodeId(e.currentTarget.value)}
              >
                <For each={depthFirst(nodes())}>
                  {({ node, depth }) => (
                    <option
                      value={node.id}
                    >{`${"  ".repeat(depth)}${node.name}`}</option>
                  )}
                </For>
              </select>
            </div>
          </Show>
          <Show when={free().length > 0}>
            <div class="seg" role="group" aria-label={t("assets.point")}>
              <button
                type="button"
                aria-pressed={mode() === "new"}
                onClick={() => setMode("new")}
              >
                {t("place.newPoint")}
              </button>
              <button
                type="button"
                aria-pressed={mode() === "existing"}
                onClick={() => setMode("existing")}
              >
                {t("place.existingPoint")}
              </button>
            </div>
          </Show>
          <Show
            when={mode() === "new" || free().length === 0}
            fallback={
              <div class="field">
                <label for="placePoint">{t("assets.point")}</label>
                <select
                  id="placePoint"
                  value={chosenPoint()}
                  onChange={(e) => setPointId(e.currentTarget.value)}
                >
                  <For each={free()}>
                    {(p) => (
                      <option
                        value={p.id}
                      >{`${p.name} · ${t(`role.point.${p.role}`)}`}</option>
                    )}
                  </For>
                </select>
              </div>
            }
          >
            <div class="field">
              <label for="placeName">{t("place.pointName")}</label>
              <input
                id="placeName"
                required
                maxLength={120}
                value={name() ?? device()?.name ?? ""}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </div>
            <Show
              when={!condition()}
              fallback={
                <p class="hint" style={{ margin: 0 }}>
                  {t("place.measures", {
                    quantity: t(`medium.${kind()!.medium}`),
                  })}
                </p>
              }
            >
              <div class="g-2e grid" style={{ gap: "10px" }}>
                <div class="field">
                  <label for="placeRole">{t("assets.role")}</label>
                  <select
                    id="placeRole"
                    value={chosenRole()}
                    onChange={(e) => setRole(e.currentTarget.value)}
                  >
                    <For each={roles()}>
                      {(r) => <option value={r}>{t(`role.point.${r}`)}</option>}
                    </For>
                  </select>
                </div>
                <Show when={mediaChoice().length > 1}>
                  <div class="field">
                    <label for="placeMedium">{t("assets.medium")}</label>
                    <select
                      id="placeMedium"
                      value={chosenMedium()}
                      onChange={(e) => setMedium(e.currentTarget.value)}
                    >
                      <For each={mediaChoice()}>
                        {(m) => <option value={m}>{t(`medium.${m}`)}</option>}
                      </For>
                    </select>
                  </div>
                </Show>
              </div>
              <p class="hint" style={{ margin: 0 }}>
                {t(`role.pointHint.${chosenRole()}`)}
              </p>
            </Show>
          </Show>
          <Show when={mode() === "new" && plan().length > 1}>
            <label class="opt">
              <input
                type="checkbox"
                checked={whole()}
                onChange={(e) => setWhole(e.currentTarget.checked)}
              />
              <span>
                <b>
                  {t(
                    planHasMeters() ? "place.whole" : "place.wholeQuantities",
                    { count: plan().length },
                  )}
                </b>
                <small>
                  {t(
                    planHasMeters()
                      ? "place.wholeHint"
                      : "place.wholeQuantitiesHint",
                  )}
                </small>
              </span>
            </label>
            <Show when={whole()}>
              <ul class="device-plan">
                <For each={plan()}>
                  {(p) => (
                    <li>
                      <span>{p.name}</span>
                      <span class="hint">
                        {p.kind === "condition"
                          ? t(`medium.${p.medium}`)
                          : t(`role.point.${p.role}`)}
                      </span>
                    </li>
                  )}
                </For>
              </ul>
            </Show>
          </Show>
          <Show when={channels().length > 1 && !placingWhole()}>
            <div class="field">
              <label for="placeChannel">{t("assets.channel")}</label>
              <select
                id="placeChannel"
                value={chosenChannel()}
                onChange={(e) => setChannel(e.currentTarget.value)}
              >
                <For each={channels()}>
                  {(c) => (
                    <option value={c.key}>
                      {tOr(`channel.${c.key}`, c.key)}
                    </option>
                  )}
                </For>
              </select>
            </div>
          </Show>
          <Show when={located()}>
            {(at) => (
              <label class="opt">
                <input
                  type="checkbox"
                  checked={useLocation()}
                  onChange={(e) => setUseLocation(e.currentTarget.checked)}
                />
                <span>
                  <b>{t("place.useLocation", { name: node()!.name })}</b>
                  <small>
                    {t("place.useLocationHint", {
                      at: `${at().latitude.toFixed(5)}, ${at().longitude.toFixed(5)}`,
                    })}
                  </small>
                </span>
              </label>
            )}
          </Show>
          <Show when={hint()?.role && !role()}>
            <p class="hint" style={{ margin: 0 }}>
              {t("place.roleFromIntegration")}
            </p>
          </Show>
          <Show when={error()}>
            <ErrorBox error={error()} />
          </Show>
          <div class="row-acts" style={{ "justify-content": "flex-end" }}>
            <button type="button" class="btn" onClick={() => props.onClose()}>
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              class="btn primary"
              disabled={
                busy() || !deviceId() || !nodeId() || channels().length === 0
              }
            >
              {t("assets.bind")}
            </button>
          </div>
        </form>
      </Show>
    </Dialog>
  );
}
